import React, { useState } from 'react';
import './PipelineStageEditor.css';

/**
 * Per-product-type pipeline stage editor (sales rework Phase 2) — the first
 * UI over the admin pipeline-stage API. Won / Final are exclusive per product
 * type: the server clears the flag from siblings when set here, and refuses
 * to unset the last one.
 *
 * Orders (spec part B, decision 11, 2026-10-16): "Orders" ticks the stages an
 * order moves through; "Order start" (one per product, like Won) is the stage
 * an order is created on. The start stage is always ticked.
 */
function PipelineStageEditor({ productType, stages = [], phases = [], canEdit, authFetch, onChanged }) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [newStage, setNewStage] = useState({ name: '', phase: '' });

  const sorted = [...stages].sort((a, b) => a.sort_order - b.sort_order);

  const call = async (path, options) => {
    setBusy(true);
    setError('');
    try {
      const response = await authFetch(path, options);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Stage update failed');
      }
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const updateStage = (stageId, changes) =>
    call(`/api/v1/admin/pipeline-stages/${stageId}`, { method: 'PUT', body: JSON.stringify(changes) });

  const moveStage = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sorted.length) return;
    const reordered = [...sorted];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    return call('/api/v1/admin/pipeline-stages/reorder', {
      method: 'PUT',
      body: JSON.stringify({
        stages: reordered.map((stage, i) => ({ stage_id: stage.stage_id, sort_order: (i + 1) * 10 }))
      })
    });
  };

  const nextRef = () => {
    const used = new Set(sorted.map(stage => stage.stage_ref));
    for (let i = 0; i < 26; i += 1) {
      const ref = String.fromCharCode(65 + i);
      if (!used.has(ref)) return ref;
    }
    return `S${sorted.length + 1}`;
  };

  const addStage = () => {
    if (!newStage.name.trim() || !newStage.phase) {
      setError('New stage needs a name and a phase');
      return;
    }
    return call('/api/v1/admin/pipeline-stages', {
      method: 'POST',
      body: JSON.stringify({
        product_type: productType,
        stage_ref: nextRef(),
        stage_name: newStage.name.trim(),
        phase: newStage.phase
      })
    }).then(() => setNewStage({ name: '', phase: '' }));
  };

  const deleteStage = (stageId) =>
    call(`/api/v1/admin/pipeline-stages/${stageId}`, { method: 'DELETE' });

  return (
    <div className="stage-editor">
      <div className="config-subheading">Pipeline stages</div>
      <p className="stage-editor-hint">
        Line items of this product type move through these stages. <strong>Won</strong> marks
        the deal won (and creates the asset where asset generation is on); <strong>Final</strong> is
        the stage after which the line item leaves the pipeline. Schedule ✓ demands a calendar
        entry on progression; PO ✓ allows purchase-order upload at that stage.
        <strong> Orders</strong> ✓ are the stages an order (a purchase already agreed) moves through,
        and <strong>Order start</strong> is the stage an order is created on.
      </p>
      {error && <div className="stage-editor-error">{error}</div>}
      <table className="stage-editor-table">
        <thead>
          <tr>
            <th></th>
            <th>Stage</th>
            <th>Phase</th>
            <th>Prob %</th>
            <th title="Deal is won at this stage">Won</th>
            <th title="Line item exits the pipeline after this stage">Final</th>
            <th title="Requires a schedule entry to progress">Schedule</th>
            <th title="PO upload available at this stage">PO</th>
            <th title="Orders move through this stage">Orders</th>
            <th title="An order is created on this stage">Order start</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((stage, index) => (
            <tr key={stage.stage_id} className={stage.active === false ? 'stage-inactive' : ''}>
              <td className="stage-order">
                {canEdit && (
                  <>
                    <button disabled={busy || index === 0} onClick={() => moveStage(index, -1)} title="Move up">↑</button>
                    <button disabled={busy || index === sorted.length - 1} onClick={() => moveStage(index, 1)} title="Move down">↓</button>
                  </>
                )}
              </td>
              <td>
                <input
                  defaultValue={stage.stage_name}
                  disabled={!canEdit || busy}
                  onBlur={(e) => {
                    const name = e.target.value.trim();
                    if (name && name !== stage.stage_name) updateStage(stage.stage_id, { stage_name: name });
                  }}
                />
              </td>
              <td>
                <select
                  value={stage.phase}
                  disabled={!canEdit || busy}
                  onChange={(e) => updateStage(stage.stage_id, { phase: e.target.value })}
                >
                  {phases.map(phase => (
                    <option key={phase} value={phase}>{phase.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="number" min="0" max="100" className="stage-prob"
                  defaultValue={stage.default_probability_pct}
                  disabled={!canEdit || busy}
                  onBlur={(e) => {
                    const pct = parseInt(e.target.value, 10);
                    if (Number.isInteger(pct) && pct !== stage.default_probability_pct) {
                      updateStage(stage.stage_id, { default_probability_pct: pct });
                    }
                  }}
                />
              </td>
              <td className="stage-flag">
                <input
                  type="radio"
                  name={`won-${productType}`}
                  checked={stage.is_closed_won === true}
                  disabled={!canEdit || busy}
                  onChange={() => updateStage(stage.stage_id, { is_closed_won: true })}
                />
              </td>
              <td className="stage-flag">
                <input
                  type="radio"
                  name={`final-${productType}`}
                  checked={stage.is_final === true}
                  disabled={!canEdit || busy}
                  onChange={() => updateStage(stage.stage_id, { is_final: true })}
                />
              </td>
              <td className="stage-flag">
                <input
                  type="checkbox"
                  checked={stage.requires_schedule_entry === true}
                  disabled={!canEdit || busy}
                  onChange={(e) => updateStage(stage.stage_id, { requires_schedule_entry: e.target.checked })}
                />
              </td>
              <td className="stage-flag">
                <input
                  type="checkbox"
                  checked={stage.po_upload_enabled === true}
                  disabled={!canEdit || busy}
                  onChange={(e) => updateStage(stage.stage_id, { po_upload_enabled: e.target.checked })}
                />
              </td>
              <td className="stage-flag">
                <input
                  type="checkbox"
                  aria-label={`Orders use ${stage.stage_name}`}
                  checked={stage.used_by_orders === true}
                  disabled={!canEdit || busy || stage.is_order_start === true}
                  title={stage.is_order_start ? 'Orders start here, so they always use it' : undefined}
                  onChange={(e) => updateStage(stage.stage_id, { used_by_orders: e.target.checked })}
                />
              </td>
              <td className="stage-flag">
                <input
                  type="radio"
                  name={`order-start-${productType}`}
                  aria-label={`Orders start at ${stage.stage_name}`}
                  checked={stage.is_order_start === true}
                  disabled={!canEdit || busy}
                  onChange={() => updateStage(stage.stage_id, { is_order_start: true })}
                />
              </td>
              <td>
                {canEdit && (
                  <button
                    className="btn-danger btn-small"
                    disabled={busy || stage.is_closed_won || stage.is_final || stage.is_order_start}
                    title={stage.is_closed_won || stage.is_final || stage.is_order_start ? 'Move the Won/Final/Order start flag first' : 'Delete stage'}
                    onClick={() => deleteStage(stage.stage_id)}
                  >
                    ×
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit && (
        <div className="stage-editor-add">
          <input
            placeholder="New stage name"
            value={newStage.name}
            disabled={busy}
            onChange={(e) => setNewStage(prev => ({ ...prev, name: e.target.value }))}
          />
          <select
            value={newStage.phase}
            disabled={busy}
            onChange={(e) => setNewStage(prev => ({ ...prev, phase: e.target.value }))}
          >
            <option value="">Phase…</option>
            {phases.map(phase => (
              <option key={phase} value={phase}>{phase.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <button className="btn-secondary btn-small" disabled={busy} onClick={addStage}>+ Add Stage</button>
        </div>
      )}
    </div>
  );
}

export default PipelineStageEditor;
